import React, { type JSX } from 'react';
import { action, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
import { type CategoryInfo } from '../models/CategoryInfo';

export type CategoryContext = {
  close: () => void;
};

type CategoryPickerProps = {
  categories: CategoryInfo[];
  activeCategoryId: string;
  onRenderText?: (context: CategoryContext) => JSX.Element;
  onRenderItems?: (context: CategoryContext) => JSX.Element;
  onCategoryChanged: (category: CategoryInfo) => void;
};

@observer
export default class CategoryPicker extends React.Component<CategoryPickerProps> {
  @observable private _isOpen = false;
  private _containerRef: React.RefObject<HTMLDivElement> = React.createRef();

  constructor(props: CategoryPickerProps){
    super(props);
    makeObservable(this);
  }

  async componentDidMount(): Promise<void>{
    document.addEventListener('click', this._onDocumentClick);
  }

  componentWillUnmount(){
    document.removeEventListener('click', this._onDocumentClick);
  }

  render(): JSX.Element{
    const { categories, activeCategoryId, onRenderText, onRenderItems } = this.props;

    let itemsClass = 'items';
    if (!this._isOpen){
      itemsClass += ' hide';
    }

    let dropdownClass = 'dropdown-icon';
    if (this._isOpen){
      dropdownClass += ' open';
    }

    const context: CategoryContext = {
      close: () => {
        this._isOpen = false;
      }
    };

    const activeCategory = categories.find(c => c.id === activeCategoryId);
    return (
      <div className="category-select" ref={this._containerRef}>
        <div className="item-selected" onClick={this._onClick}>
          <span>{activeCategory?.name || '[No name]'}</span>
          <div className={dropdownClass}></div>
          {onRenderText?.(context)}
        </div>
        <div className={itemsClass}>
          {categories.map(c => {
            let itemClass = "item";
            if (c.id === activeCategoryId){
              itemClass += ' item-selected';
            }
            return (<div key={c.id} className={itemClass} onClick={() => this._onCategorySelected(c)}>{c.name || '[No name]'}</div>);
          })}
          {onRenderItems?.(context)}
        </div>
      </div>
    );
  }

  @action.bound
  private _onClick(){
    this._isOpen = !this._isOpen;
  }

  @action.bound
  private _onDocumentClick(e: MouseEvent){
    if (e.target && this._containerRef.current?.contains(e.target as Node)){
      return;
    }

    this._isOpen = false;
  }

  @action.bound
  private _onCategorySelected(c: CategoryInfo){
    this.props.onCategoryChanged(c);
    this._isOpen = false;
  }
}